import * as React from 'react';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { cn } from '@/lib/cn';

const MOBILE_BREAKPOINT_PX = 768;
let bodyScrollLockCount = 0;
let bodyOriginalOverflow = '';

function lockBodyScroll() {
  if (bodyScrollLockCount === 0) {
    bodyOriginalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyScrollLockCount += 1;
}

function unlockBodyScroll() {
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
  if (bodyScrollLockCount === 0) {
    document.body.style.overflow = bodyOriginalOverflow;
  }
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const apply = () => setIsMobile(mql.matches);
    apply();
    mql.addEventListener?.('change', apply);
    return () => mql.removeEventListener?.('change', apply);
  }, []);
  return isMobile;
}

/**
 * Drawer — portal counterpart to system-web's `checkout/upsell-drawer.js`
 * shell. Mobile slides up from the bottom (h-[calc(100vh-4rem)]); desktop
 * slides in from the right (40rem panel). Backdrop blur + body-scroll lock
 * + Esc-to-close. Header is a sticky 40px row with a mono-caps eyebrow
 * label on one side and a gray-square X on the other.
 *
 * Children render inside a single scroll area so headers, content, and
 * inline summaries all scroll together. Use the `<Drawer.Footer>` slot
 * for a sticky bottom CTA.
 */
interface DrawerProps {
  open: boolean;
  onClose?: () => void;
  title: string;
  /** Hide the close X (e.g. for un-dismissible required flows). */
  hideClose?: boolean;
  /** ARIA label for the drawer dialog. Defaults to `title`. */
  ariaLabel?: string;
  children: React.ReactNode;
  /** Sticky bottom slot — typically a full-width CTA button. */
  footer?: React.ReactNode;
  /** Wider desktop surface for full-form and chart workflows. */
  size?: 'default' | 'wide';
  /** Raise a drawer above another open drawer. */
  layer?: 'default' | 'nested';
}

export function Drawer({
  open,
  onClose,
  title,
  hideClose,
  ariaLabel,
  children,
  footer,
  size = 'default',
  layer = 'default',
}: DrawerProps) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!open) return;
    lockBodyScroll();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !hideClose) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      unlockBodyScroll();
      document.removeEventListener('keydown', onKey);
    };
  }, [open, hideClose, onClose]);

  const enterFrom = isMobile ? { y: '100%', x: 0 } : { x: '100%', y: 0 };
  const restPos = { y: 0, x: 0 };
  const drawerLayoutClasses = isMobile
    ? `fixed left-0 right-0 bottom-0 ${layer === 'nested' ? 'z-[101]' : 'z-[81]'} w-full h-[calc(100vh-4rem)] overflow-hidden`
    : cn(
        `fixed right-0 bottom-0 top-0 ${layer === 'nested' ? 'z-[101]' : 'z-[81]'} w-full max-h-[100vh] border-l border-line-subtle overflow-hidden`,
        size === 'wide' ? 'md:w-[min(80rem,calc(100vw-3rem))]' : 'md:w-[40rem]',
      );

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={hideClose ? undefined : onClose}
            className={`fixed inset-0 ${layer === 'nested' ? 'z-[100]' : 'z-[80]'} bg-[#888888]/30 backdrop-blur-[10px]`}
            aria-hidden
          />
          <motion.aside
            key="drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel ?? title}
            initial={enterFrom}
            animate={restPos}
            exit={enterFrom}
            transition={{ type: 'tween', duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
            className={cn(drawerLayoutClasses, 'bg-white flex flex-col')}
          >
            <div
              className="flex-1 overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <DrawerHeader title={title} onClose={onClose} hideClose={hideClose} />
              {children}
            </div>
            {footer ? (
              <div
                className="shrink-0 w-full"
                style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
              >
                {footer}
              </div>
            ) : null}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function DrawerHeader({
  title,
  onClose,
  hideClose,
}: {
  title: string;
  onClose?: () => void;
  hideClose?: boolean;
}) {
  return (
    <div className="bg-[#F0F0F0] border-b border-[#ECECEC] flex items-center w-full sticky top-0 left-0 z-[3]">
      <div className="flex-1 min-w-0 flex items-center px-15 h-40">
        <p className="font-mono text-12 text-[#5E5E5E] uppercase leading-105">
          {title}
        </p>
      </div>
      {!hideClose && (
        <div className="p-[2px] h-40">
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            className="bg-slate flex items-center justify-center w-[36px] h-full hover:bg-[#CCCCCC]"
          >
            <span className="block w-[14px] h-[14px] relative">
              <span className="absolute left-0 top-1/2 w-[14px] h-[1.5px] bg-black rotate-45 origin-center" />
              <span className="absolute left-0 top-1/2 w-[14px] h-[1.5px] bg-black -rotate-45 origin-center" />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
